import {
  point,
  points,
  multiPoint,
  polygon,
  lineString,
  multiLineString,
  featureCollection,
} from '@turf/helpers'
import turfLength from '@turf/length'
import turfMidpoint from '@turf/midpoint'
import turfCleanCoords from '@turf/clean-coords'
import turfLineSegment from '@turf/line-segment'
import turfExplode from '@turf/explode'
import turfArea from '@turf/area'
import turfCenterOfMass from '@turf/center-of-mass'
import turfPolygonToLine from '@turf/polygon-to-line'
import turfBuffer from '@turf/buffer'
import turfEnvelope from '@turf/envelope'
import turfBBox from '@turf/bbox'
import turfBBoxPoly from '@turf/bbox-polygon'
import turfPointsWithinPolygon from '@turf/points-within-polygon'
import turfPointOnFeature from '@turf/point-on-feature'
import turfNearestPointOnLine from '@turf/nearest-point-on-line'
import turfNearestPoint from '@turf/nearest-point'
import turfDissolve from '@turf/dissolve'
import turfLineToPolygon from '@turf/line-to-polygon'
import { coordAll } from '@turf/meta'
import cloneDeep from 'lodash/cloneDeep'
import { POINT_FEATURE } from '@/data/Constants/Constants'

export const clone = value => cloneDeep(value)

/**
 * Round a number to specified decimal places
 * @param {number} value - Value to round
 * @param {number} decimals - Number of decimal places
 */
const round = (value, decimals) =>
  Number(Math.round(value + 'e' + decimals) + 'e-' + decimals)

/**
 * Convert screen coords to [lng, lat] using MapLibre map instance
 * @param {number} x - Screen X coordinate
 * @param {number} y - Screen Y coordinate
 * @param {object} map - MapLibre map instance
 * @returns {[number, number] | null} [longitude, latitude] or null if map unavailable
 */
export const getMouseCoords = (x, y, map) => {
  if (!map || typeof map.unproject !== 'function') return null
  const lngLat = map.unproject([x, y])
  return [round(lngLat.lng, 7), round(lngLat.lat, 7)]
}

/**
 * Get a string representation of the map center for comparison
 * Accepts either a viewport object or a MapLibre map instance
 * @param {object} viewportOrMap - Viewport object with latitude/longitude OR MapLibre map instance
 * @returns {string} Concatenated lat+lng string for comparison
 */
export const getMapCenter = viewportOrMap => {
  if (!viewportOrMap) return ''
  
  // Check if it's a MapLibre map instance (has getCenter method)
  if (typeof viewportOrMap.getCenter === 'function') {
    const center = viewportOrMap.getCenter()
    const lat = center.lat.toFixed(6).toString()
    const lng = center.lng.toFixed(6).toString()
    return lat + lng
  }
  
  // Otherwise treat as viewport object
  const lat = viewportOrMap.latitude?.toFixed(6)?.toString() || ''
  const lng = viewportOrMap.longitude?.toFixed(6)?.toString() || ''
  return lat + lng
}

/**
 * Get a GeoJSON point feature at the click location
 * @param {object} event - Mouse event with layerX/offsetX and layerY/offsetY
 * @param {object} map - MapLibre map instance
 * @returns {object} GeoJSON Point feature
 */
export const getPointAtClick = (event, map) => {
  const coordinates = getMouseCoords(
    event.layerX || event.offsetX,
    event.layerY || event.offsetY,
    map
  )
  const pointClone = clone(POINT_FEATURE)
  pointClone.geometry.coordinates = coordinates
  return pointClone
}

/**
 * Get [lng, lat] coordinates at the click location
 * @param {object} event - Mouse event with layerX/offsetX and layerY/offsetY
 * @param {object} map - MapLibre map instance
 * @returns {[number, number] | null} [longitude, latitude]
 */
export const getCoordsAtClick = (event, map) => {
  const coordinates = getMouseCoords(
    event.layerX || event.offsetX,
    event.layerY || event.offsetY,
    map
  )
  return coordinates
}

export const getLength = (feature, units) => {
  const options = { units }
  const len = turfLength(feature, options)
  return len
}

export const getArea = feature => {
  const area = turfArea(feature)
  return area
}

export const getLineMidPoint = feature => {
  const point1 = point(feature.geometry.coordinates[0])
  const point2 = point(feature.geometry.coordinates[1])
  const mid = turfMidpoint(point1, point2)
  mid.properties.pt1 = point1.geometry.coordinates.join(',')
  mid.properties.pt2 = point2.geometry.coordinates.join(',')
  return mid
}

export const getLineEndPoint = feature => {
  const endPoints = turfExplode(feature)
  const endPoint = endPoints.features[endPoints.features.length - 1]
  return endPoint
}

export const getCleanCoords = feature => {
  const clean = turfCleanCoords(feature)
  return clean
}

export const getLineString = coords => {
  const line = lineString(coords)
  return line
}

export const getMultiLineString = coords => {
  const multiLine = multiLineString(coords)
  return multiLine
}

export const getPolygon = coords => {
  const poly = polygon(coords)
  return poly
}

export const getPoint = coords => {
  const pt = point(coords)
  return pt
}

export const getFeatureCollection = features => {
  const fc = featureCollection(features)
  return fc
}

export const getCoordinates = (feature, precision) => {
  // Check for multiPoint or singlePoint with coordinates array.
  const coordinates =
    feature.geometry.coordinates.length === 1
      ? feature.geometry.coordinates[0]
      : feature.geometry.coordinates

  const lat = coordinates[1]
  const lng = coordinates[0]
  return { lat: lat.toFixed(precision), lng: lng.toFixed(precision) }
}

export const getLineSegments = feature => {
  const segments = turfLineSegment(feature)
  return segments
}

export const lineToPolygon = feature => {
  return turfLineToPolygon(feature)
}
export const polygonToLine = feature => {
  const line = turfPolygonToLine(feature)
  return line
}

export const getBuffer = (feature, distance, units) => {
  if (units !== 'feet' && units !== 'miles') {
    console.warn('Units must be feet || miles')
    return null
  }
  let dist = distance
  if (units === 'feet') {
    dist = distance / 5280
  }
  const buffered = turfBuffer(feature, dist, { units: 'miles' })
  return buffered
}

export const getEnvelope = features => {
  const enveloped = turfEnvelope(features)
  return enveloped
}

export const getBBox = features => {
  const bBox = turfBBox(features)
  return bBox
}

/**
 * combines multiple bounds into a single bounding box
 * @param {Array} boundsArray array of bounds [[x1, y1]...]
 * @returns {Array|null} combined bounds as [[minX, minY], [maxX, maxY]] or null if invalid
 */
export function combineBounds(boundsArray) {
  if (!Array.isArray(boundsArray) || boundsArray.length === 0) {
    return null
  }

  try {
    const features = boundsArray.map(bounds => ({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: bounds
      }
    }))
    
    const featureCollection = {
      type: 'FeatureCollection',
      features: features
    }

    const combinedBounds = getBBox(featureCollection)
    
    if (combinedBounds && combinedBounds.length === 4) {
      return [
        [combinedBounds[0], combinedBounds[1]],
        [combinedBounds[2], combinedBounds[3]]
      ]
    }
    
    return null
  } catch (error) {
    return null
  }
}

export const getBBoxPoly = bbox => {
  const poly = turfBBoxPoly(bbox)
  return poly
}

export const getPointsWithinPolygon = (pointsToCheck, polygonToCheck) => {
  const ptsWithin = turfPointsWithinPolygon(pointsToCheck, polygonToCheck)
  return ptsWithin
}

export const getMultiPointFeatureFromCoords = coords => {
  return multiPoint([coords])
}

export const getPointsFromCoords = coords => {
  return points(coords)
}

export const explodeFeature = feature => {
  return turfExplode(feature)
}

export const getAllCoords = coordFeatureCollection => {
  return coordAll(coordFeatureCollection)
}

export const getCenterOfMass = feature => {
  let center
  try {
    if (feature.geometry.type === 'MultiPolygon') {
      const allCoords = getAllCoords(feature)
      const allPoints = allCoords.map(coords => point(coords))
      const collection = featureCollection(allPoints)
      const envelope = getEnvelope(collection)
      center = turfCenterOfMass(envelope)
    } else {
      center = turfCenterOfMass(feature)
    }
  } catch (err) {
    console.log('turf error ', err)
    center = null
  }
  return center
}

export const firstAndLastCoordEquivalent = coords => {
  const first = coords[0]
  const last = coords[coords.length - 1]
  if (JSON.stringify(first) === JSON.stringify(last)) return true
  return false
}

export const getNearestPointOnLine = (line, pt) => {
  const nearest = turfNearestPointOnLine(line, pt, { units: 'miles' })
  return nearest
}

export const getNearestVertex = (line, pt) => {
  const allPoints = explodeFeature(line)
  const nearest = turfNearestPoint(pt, allPoints)
  return nearest
}

export const dissolveFeatures = (features, propertyObject = {}) => {
  const dissolved = turfDissolve(features, propertyObject)
  return dissolved
}

export const getPointOnFeature = feature => {
  const pointOnFeature = turfPointOnFeature(feature)
  return pointOnFeature
}

/**
 * Formats bounds from Google Maps API response format into the format needed for map operations
 *
 * @param {Object} googleBounds - Bounds object from Google Maps API
 * @returns {Array} Array of coordinate pairs in format [[minLng, minLat], [maxLng, maxLat]] or null if invalid
 */
export const formatGoogleBounds = bounds => {
  const formatted = []
  const lats = []
  const lngs = []
  for (const [, coordSet] of Object.entries(bounds)) {
    if (coordSet && typeof coordSet === 'object') {
      Object.values(coordSet).forEach(value => {
        if (lats.length < 2) {
          lats.push(value)
        } else {
          lngs.push(value)
        }
      })
    }
  }
  if (lngs.length >= 2 && lats.length >= 2) {
    const northEast = [
      parseFloat(lngs[0].toFixed(6)),
      parseFloat(lats[0].toFixed(6)),
    ]
    const southWest = [
      parseFloat(lngs[1].toFixed(6)),
      parseFloat(lats[1].toFixed(6)),
    ]
    formatted.push(northEast)
    formatted.push(southWest)
  }
  return formatted
}
